import { useEffect, createContext, useState, useRef, useCallback } from "react";
import { parseCookies, setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import { GetSettings } from "../services/AccountServices";
import { api } from "../api/Middleware";
import { getUserPermissions } from "../screens/Users/services/UserServicesV2";
import {
  getPlanCategory,
  isClientPlan,
  isStarterPlan,
  isScalePlan,
  hasFeatureAccess,
  getPlanLabel,
  getDefaultPlan,
} from "../utils/PlanUtils";
import { GetAccounts } from "../services/WaBottyServices";
import InstagramApiService from "../screens/InstagramConfig/services/InstagramApiService";

const UserContext = createContext();

/**
 * Versão do cache de permissões em memória.
 * Bumpar quando adicionar nova feature ao sistema, pra invalidar caches
 * persistentes (caso sejam adicionados no futuro). O cache atual é useRef,
 * então não persiste entre reloads — mas a constante é mantida como
 * defesa em profundidade.
 */
const PERMISSIONS_CACHE_VERSION = 'v2';

export function UserProvider({ children, userName, setIsLoading }) {
  const { token: _token } = parseCookies();
  const { MA_AID: account_id } = parseCookies();

  // Verificar se é uma rota pública
  const publicRoutes = ["/login", "/register", "/activate", "/checkout", "/signup", "/politica-de-privacidade", "/termos-de-uso"];
  const currentPath = window.location.pathname;
  const isPublicRoute =
    publicRoutes.includes(currentPath) || 
    currentPath.startsWith("/testar") || 
    currentPath.startsWith("/conectar");

  // Estados principais
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(_token || null);
  const [logged, setLogged] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // NOVO: Estado de inicialização
  const [bottyData, setBottyData] = useState();
  const [accountLoading, setAccountLoading] = useState(true);

  // Estados para novo JWT
  const [accountContext, setAccountContext] = useState(null);
  const [accountContextLoaded, setAccountContextLoaded] = useState(false);
  const [accountFeatures, setAccountFeatures] = useState([]);

  // Estados para nova estrutura de permissões por ID
  const [userFeatures, setUserFeatures] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(null);

  // Estado para o plano do usuário
  const [userPlan, setUserPlan] = useState(null);

  // Estado para verificar se é uma sessão de suporte
  const [isSupportSession, setIsSupportSession] = useState(false);

  // Estados para acesso restrito por plano
  const [restrictedAccess, setRestrictedAccess] = useState(false);
  const [restrictionReason, setRestrictionReason] = useState(null);

  const settings = useRef();

  // Cache em memória (mais seguro que sessionStorage)
  const permissionsCache = useRef(new Map());

  // Função para obter configurações da conta
  const getAccountSettings = async () => {
    try {
      settings.current = await GetSettings();
      if (settings.current) {
        localStorage.setItem("__accsettings", JSON.stringify(settings.current));
      }
    } catch (error) {
      console.error("❌ Erro ao carregar configurações da conta:", error);
    }
  };

  const superuser = {
    1: "Admin",
    2: "Gerente",
    3: "Supervisor",
    4: "Atendente",
  };

  // Funções de cache em memória (mais seguro)
  const getCachedPermissions = (userId) => {
    if (!userId) return null;

    try {
      const cached = permissionsCache.current.get(userId);
      if (!cached) return null;

      // Invalidar caches em versão antiga (defesa contra refator de schema)
      if (cached.version !== PERMISSIONS_CACHE_VERSION) {
        permissionsCache.current.delete(userId);
        return null;
      }

      // Verificar se expirou
      if (Date.now() > cached.expiry) {
        permissionsCache.current.delete(userId);
        return null;
      }

      return cached;
    } catch {
      return null;
    }
  };

  const setCachedPermissions = (userId, permissions, minutes) => {
    if (!userId || !permissions) return;

    const expiry = Date.now() + minutes * 60 * 1000;
    const data = { permissions, expiry, version: PERMISSIONS_CACHE_VERSION };

    try {
      permissionsCache.current.set(userId, data);
    } catch (error) {
      // Erro silencioso no cache
    }
  };

  const isExpired = (cached) => {
    return Date.now() > cached.expiry;
  };

  const clearPermissionsCache = (userId) => {
    if (userId) {
      permissionsCache.current.delete(userId);
    } else {
      // Limpar todo o cache
      permissionsCache.current.clear();
    }
  };

  // Função para carregar permissões do usuário
  const loadUserPermissions = useCallback(
    async (force = false) => {
      if (!user?.user_id) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        setPermissionsError(null);

        // Verificar cache primeiro (se não for forçado)
        if (!force) {
          const cached = getCachedPermissions(user.user_id);
          if (cached && !isExpired(cached)) {
            setUserFeatures(cached.permissions.features || {});
            setPermissionsLoading(false);
            return;
          }
        }

        // Carregar da API
        const response = await getUserPermissions();
        const data = response.data;

        // Atualizar estado com nova estrutura
        setUserFeatures(data.features || {});

        // Cache por 10 minutos
        setCachedPermissions(user.user_id, data, 10);
      } catch (error) {
        console.error("❌ Erro ao carregar permissões:", error);
        setPermissionsError(error);

        // Fallback: limpar features
        setUserFeatures(null);
      } finally {
        setPermissionsLoading(false);
      }
    },
    [user?.user_id]
  );

  // Função para verificar permissões por ID da feature
  const hasPermission = (featureId, action = "read") => {
    if (!user || !featureId) {
      return false;
    }

    // Admin sempre tem acesso total
    if (user.superuser_level === 1) {
      return true;
    }

    // Verificar permissões por ID da feature
    if (userFeatures && userFeatures[featureId]) {
      const hasAccess = userFeatures[featureId].permissions[action] || false;
      return hasAccess;
    }

    // Para usuários level=0, APENAS usar permissões da API
    if (user.superuser_level === 0) {
      return false;
    }

    // Fallback: negar acesso se feature não encontrada
    return false;
  };

  /**
   * Busca permissão por SLUG (recomendado pra features novas).
   * Evita hardcode de ID numérico no frontend — IDs podem variar entre ambientes.
   * O backend (`get_user_permissions_v2`) já retorna `slug` em cada feature.
   */
  const hasPermissionBySlug = (slug, action = "read") => {
    if (!user || !slug) return false;
    if (user.superuser_level === 1) return true; // admin
    if (!userFeatures) return false;
    const found = Object.values(userFeatures).find((f) => f.slug === slug);
    if (!found) return false;
    return found.permissions?.[action] === true;
  };

  // Função para obter informações de uso de uma feature
  const getUsageInfo = (featureId) => {
    if (!userFeatures || !userFeatures[featureId]) {
      return null;
    }
    return userFeatures[featureId].usage_info || null;
  };

  // Função para verificar se pode executar ação considerando limites de uso
  const canPerformActionWithUsage = (featureId, action = "read") => {
    // Primeiro verificar permissão básica
    const hasBasicPermission = hasPermission(featureId, action);
    if (!hasBasicPermission) {
      return {
        allowed: false,
        reason: "no_permission",
        feature: userFeatures?.[featureId] || null,
      };
    }

    // Verificar limites de uso
    const usage = getUsageInfo(featureId);
    if (usage?.has_limit && usage?.is_exceeded) {
      return {
        allowed: false,
        reason: "usage_exceeded",
        usage,
        feature: userFeatures[featureId],
      };
    }

    return {
      allowed: true,
      usage,
      feature: userFeatures?.[featureId] || null,
    };
  };

  // Função para obter feature por ID (ATUALIZADA para nova estrutura)
  const getFeatureById = (featureId) => {
    const feature = userFeatures?.[featureId];
    if (!feature) return null;

    // Retornar estrutura completa com informações da feature
    return {
      id: feature.id || featureId,
      name: feature.name,
      slug: feature.slug,
      description: feature.description,
      category: feature.category,
      permissions: feature.permissions || {},
      usage_info: feature.usage_info || null,
    };
  };

  // Função para obter todas as features por categoria
  const getFeaturesByCategory = (category) => {
    if (!userFeatures) return [];
    return Object.values(userFeatures).filter(
      (feature) => feature.category === category
    );
  };

  // Função para verificar se pode criar usuários
  const canCreateUsers = () => {
    if (!user) return false;
    return user.is_superuser && user.superuser_level <= 3;
  };

  // Função para verificar se pode gerenciar permissões
  const canManagePermissions = () => {
    if (!user) return false;
    return user.is_superuser && user.superuser_level <= 2;
  };

  // Função para verificar se pode criar usuário com determinado nível
  const canCreateUserWithLevel = (targetLevel) => {
    if (!user) return false;

    // Admin pode criar qualquer nível
    if (user.superuser_level === 1) return true;

    // Gerente pode criar supervisor e atendente
    if (user.superuser_level === 2) return [3, 4].includes(targetLevel);

    // Supervisor pode criar apenas atendente
    if (user.superuser_level === 3) return targetLevel === 4;

    return false;
  };

  // Função para obter features disponíveis do usuário
  const getAvailableFeatures = () => {
    if (!user || !userFeatures) return [];

    // Admin tem acesso a todas as features
    if (user.superuser_level === 1) {
      return Object.values(userFeatures);
    }

    // Retornar apenas features com permissão de read
    return Object.values(userFeatures).filter(
      (feature) => feature.permissions.read === true
    );
  };

  // Função para obter IDs das features disponíveis
  const getAvailableFeatureIds = () => {
    if (!user || !userFeatures) return [];

    // Admin tem acesso a todas
    if (user.superuser_level === 1) {
      return Object.keys(userFeatures);
    }

    // Retornar apenas IDs de features com permissão de read
    return Object.keys(userFeatures).filter(
      (featureId) => userFeatures[featureId].permissions.read === true
    );
  };

  // Função para atualizar permissões (útil após alterações)
  const refreshPermissions = () => {
    if (user?.user_id) {
      clearPermissionsCache(user.user_id);
      loadUserPermissions(true);
    }
  };

  // ========== FUNÇÕES PARA NOVO JWT ==========

  // Função para carregar dados do account_context e features diretamente do token decodificado
  const loadAccountData = (decodedToken) => {
    try {
      // Carregar account_context do token
      if (decodedToken.account_context) {
        setAccountContext(decodedToken.account_context);
      } else {
        setAccountContext(null);
      }

      // Carregar features do token
      if (decodedToken.features) {
        setAccountFeatures(decodedToken.features);
      } else {
        setAccountFeatures([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados da conta do token:", error);
      setAccountContext(null);
      setAccountFeatures([]);
    }
  };

  // Helpers para verificar tipo de conta
  const isPersonalAccount = () => {
    return user?.usage_type === 1;
  };
  const isOrganization = () => {
    return accountContext?.is_organization || false;
  };
  const isSubaccount = () => {
    return accountContext?.subaccount || false;
  };

  const canCreateSubaccounts = () => {
    return accountContext?.can_create_subaccounts || false;
  };

  const canDeployAgents = () => {
    return accountContext?.can_deploy_agents || false;
  };

  const getAccountType = () => {
    return accountContext?.type || "standard";
  };

  const getOrganizationInfo = () => {
    if (isOrganization()) {
      return accountContext?.organization || null;
    }
    if (isSubaccount()) {
      return accountContext?.parent_organization || null;
    }
    return null;
  };

  // Retorna o account_id da conta master (própria conta para org/normal,
  // parent_organization.account_id para subaccount).
  const getMasterAccountId = () => {
    if (isSubaccount()) {
      return accountContext?.parent_organization?.account_id || null;
    }
    return user?.account_id || null;
  };

  // Indica se a conta master usa Agentes 2.0 (/agents-flow) ou continua no
  // menu legado /agents. A versão vem do backend em `user.agents_ui_version`
  // (resolvida sempre pela conta master, mesmo quando logado em subaccount).
  const isAgentsV2Only = () => {
    return user?.agents_ui_version === "v2";
  };

  const getSubaccountLimitations = () => {
    if (isSubaccount()) {
      return accountContext?.limitations || null;
    }
    return null;
  };

  // Helpers para verificar features
  const hasFeature = (featureId) => {
    return accountFeatures.includes(featureId);
  };

  const getActiveFeatures = () => {
    return [...accountFeatures];
  };

  // Função para verificar se pode executar uma ação baseada nas features
  const canPerformAction = (
    requiredFeatureId,
    fallbackToPermissions = true
  ) => {
    // Primeiro verifica se tem a feature
    if (hasFeature(requiredFeatureId)) {
      return true;
    }

    // Fallback para sistema de permissões existente se habilitado
    if (fallbackToPermissions && user?.is_superuser) {
      return true;
    }

    return false;
  };

  // ========== FUNÇÕES HELPER PARA PLANOS ==========

  // Função para obter o plano atual do usuário
  const getUserPlan = () => {
    return user?.plan_id || userPlan || getDefaultPlan();
  };

  // Funções de verificação baseadas no plano
  const isUserClientPlan = () => {
    return isClientPlan(getUserPlan());
  };

  const isUserStarterPlan = () => {
    return isStarterPlan(getUserPlan());
  };

  const isUserScalePlan = () => {
    return isScalePlan(getUserPlan());
  };

  // Função para verificar acesso a funcionalidades baseado no plano
  const userHasFeatureAccess = (feature) => {
    return hasFeatureAccess(getUserPlan(), feature);
  };

  // Função para obter categoria do plano do usuário
  const getUserPlanCategory = () => {
    return getPlanCategory(getUserPlan());
  };

  // Função para obter label do plano do usuário
  const getUserPlanLabel = () => {
    return getPlanLabel(getUserPlan());
  };

  // Função para verificar se é uma sessão de suporte
  const checkSupportSession = () => {
    try {
      const clientAccessSession = localStorage.getItem('client_access_session');
      const hasSupportSession = !!clientAccessSession;

      setIsSupportSession(hasSupportSession);
      return hasSupportSession;
    } catch (error) {
      console.error("❌ Erro ao verificar sessão de suporte:", error);
      setIsSupportSession(false);
      return false;
    }
  };

  // Função para verificar se pode realizar ações administrativas (duplicar, excluir, ativar/desativar agentes)
  const canPerformAdminActions = () => {
    // Permitir se: plano diferente de 1 OU se é sessão de suporte
    const userPlan = getUserPlan();
    const planCondition = userPlan !== 1;
    const finalResult = planCondition || isSupportSession;
    return finalResult;
  };

  // Função para buscar contas conectadas (WhatsApp + Instagram).
  // bottyData passa a ser multi-canal: cada conta tem `provider` ∈
  // {'waha', 'cloud_api', 'instagram'}. Service Center e WebSocket
  // dependem dessa lista para rotear envio/recebimento por canal.
  const fetchWaAccount = async () => {
    try {
      // Carrega WhatsApp + Instagram em paralelo. Falha em uma não bloqueia a outra.
      const [waResult, igResult] = await Promise.allSettled([
        GetAccounts(),
        InstagramApiService.listAccounts(),
      ]);

      const accountsArray =
        waResult.status === "fulfilled" ? waResult.value : [];
      const igAccounts =
        igResult.status === "fulfilled" ? (igResult.value?.data || []) : [];

      const processedAccounts = [];

      if (Array.isArray(accountsArray)) {
        accountsArray.forEach((account) => {
          const phoneNumber =
            account.me?.id?.replace("@c.us", "") ||
            account.display_phone_number ||
            "Não disponível";

          processedAccounts.push({
            name: account.name || "default",
            status: account.status || "DISCONNECTED",
            phone: phoneNumber,
            pushName: account.me?.pushName || "Usuário",
            account_id: account.account_id || null,
            agent_id: account?.agent_id || null,
            agent_flow_id: account?.agent_flow_id || null,

            provider: account.provider || "waha",
            phone_number_id: account.phone_number_id || null,
            waba_id: account.waba_id || null,
            display_phone_number: account.display_phone_number || null,

            receive_chats: account.status === "WORKING" ? 1 : 0,
          });
        });
      }

      // Adiciona contas Instagram. Para o Service Center, `phone` é o
      // ig_business_account_id (canônico) — assim a comparação por
      // normalizePhone bate com o `to` do payload wa_notify.
      if (Array.isArray(igAccounts)) {
        igAccounts.forEach((acc) => {
          if (!acc.connected) return;
          processedAccounts.push({
            name: `instagram-${acc.username || acc.ig_business_account_id}`,
            status: acc.bot_paused ? "STOPPED" : "WORKING",
            phone: acc.ig_business_account_id,
            pushName: acc.display_name || acc.username || "Instagram",
            account_id: null,
            agent_id: null,
            agent_flow_id: null,

            provider: "instagram",
            ig_account_id: acc.id,
            ig_business_account_id: acc.ig_business_account_id,
            username: acc.username,
            display_name: acc.display_name,
            profile_picture_url: acc.profile_picture_url,
            bot_paused: acc.bot_paused,

            display_phone_number: acc.username
              ? `@${acc.username}`
              : acc.ig_business_account_id,

            receive_chats: 1,
          });
        });
      }

      sessionStorage.setItem(
        "botty_account",
        JSON.stringify(processedAccounts)
      );
      setBottyData(processedAccounts);

      if (processedAccounts.length === 0) {
        sessionStorage.removeItem("botty_account");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar contas conectadas:", error);
      setBottyData([]);
      sessionStorage.removeItem("botty_account");
    } finally {
      setAccountLoading(false);
    }
  };

  // UseEffect principal - MODIFICADO para incluir estado de inicialização
  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Para rotas públicas, não inicializar usuário
        if (isPublicRoute) {
          setIsInitializing(false);
          setLogged(false);
          return;
        }

        if (_token) {
          let _user = jwt_decode(_token);

          // Limpar campos desnecessários
          delete _user.access;
          delete _user.jti;
          delete _user.exp;
          delete _user.token_type;

          // Adicionar propriedades de permissão se não existirem
          if (!_user.hasOwnProperty("can_create_users")) {
            _user.can_create_users =
              _user.is_superuser && _user.superuser_level <= 3;
          }

          if (!_user.hasOwnProperty("can_manage_permissions")) {
            _user.can_manage_permissions =
              _user.is_superuser && _user.superuser_level <= 2;
          }

          // Adicionar is_staff se não existir (padrão false)
          if (!_user.hasOwnProperty("is_staff")) {
            _user.is_staff = false;
          }

          userName.current = _user?.first_name;

          // ========== PROCESSAMENTO DO PLANO ==========
          // Capturar plano do token (novo campo)
          if (_user.plan_id) {
            setUserPlan(_user.plan_id);
          } else {
            // Fallback padrão
            setUserPlan(getDefaultPlan());
          }

          // ========== PROCESSAMENTO DE ACESSO RESTRITO ==========
          // Verificar se o token indica acesso restrito por plano irregular
          if (_user.restricted_access === true) {
            setRestrictedAccess(true);
            setRestrictionReason(_user.restriction_reason || null);
          } else {
            setRestrictedAccess(false);
            setRestrictionReason(null);
          }

          // Definir estados do usuário com dados do JWT (renderização imediata)
          setUser(_user);
          setToken(_token);
          setLogged(true);

          // Carregar configurações da conta (não bloqueante)
          if (!settings.current) {
            getAccountSettings().catch((error) => {
              console.error("Erro ao carregar configurações:", error);
            });
          }

          // Carregar dados do account_context e features diretamente do token
          loadAccountData(_user);
          fetchWaAccount();

          // Carregar dados atualizados via /me em background
          // Prioriza dados frescos do servidor sobre o JWT decodificado
          api.get("auth/me/").then((meResponse) => {
            if (meResponse?.data) {
              const freshData = meResponse.data;
              const mergedUser = { ..._user, ...freshData };
              setUser(mergedUser);
              loadAccountData(mergedUser);

              if (mergedUser.plan_id) {
                setUserPlan(mergedUser.plan_id);
              }
            }
          }).catch((meError) => {
            // Fallback: usar dados do JWT (já setados acima)
            console.warn("Fallback para dados do JWT:", meError?.message);
          }).finally(() => {
            // Sinalizar que accountContext esta definido (com dados do /me ou fallback do JWT)
            setAccountContextLoaded(true);
          });

          // Definir cookie da conta se não existir
          if (!account_id) {
            setCookie(undefined, "MA_AID", _user.account_id, {
              maxAge: 60 * 60 * 24 * 14, // 14 dias
              path: "/",
            });
          }

          setIsLoading(false);
        } else {
          setLogged(false);
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error("❌ Erro durante inicialização do usuário:", error);
        setLogged(false);
        setUser(null);
        setToken(null);
      } finally {
        // IMPORTANTE: Marcar inicialização como completa SEMPRE
        setIsInitializing(false);
      }
    };

    // Executar inicialização
    initializeUser();
  }, [_token, isPublicRoute]);

  // UseEffect para carregar dados do sessionStorage quando usuário estiver logado
  useEffect(() => {
    if (user && logged && !isPublicRoute) {
      // Verificar se existe botty_account no sessionStorage
      try {
        const storedBottyAccount = sessionStorage.getItem("botty_account");
        
        if (storedBottyAccount) {
          const parsedBottyData = JSON.parse(storedBottyAccount);
          
          // Validar se os dados são válidos (array com estrutura correta)
          if (Array.isArray(parsedBottyData) && parsedBottyData.length > 0) {
            setBottyData(parsedBottyData);
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar botty_account do sessionStorage:", error);
        // Em caso de erro, limpar dados corrompidos
        sessionStorage.removeItem("botty_account");
        setBottyData([]);
      }
    }
  }, [user, logged, isPublicRoute]);

  // UseEffect para carregar permissões quando usuário for definido
  useEffect(() => {
    if (user?.user_id && logged) {
      loadUserPermissions();
    } else {
      setPermissionsLoading(false);
      setUserFeatures(null);
      // Limpar cache quando usuário sair
      clearPermissionsCache();
    }
  }, [user?.user_id, logged, loadUserPermissions]);

  // UseEffect para verificar sessão de suporte
  useEffect(() => {
    checkSupportSession();
  }, []);


  // UseEffect para limpar cache automaticamente ao trocar de usuário
  useEffect(() => {
    return () => {
      // Cleanup: limpar cache quando componente for desmontado
      clearPermissionsCache();
    };
  }, []);

  // ========== HELPERS PARA ACESSO RESTRITO ==========

  // Verifica se o usuário tem acesso restrito
  const isRestrictedAccess = () => restrictedAccess;

  // Obtém o motivo da restrição
  const getRestrictionReason = () => restrictionReason;

  // Rotas permitidas para usuários com acesso restrito
  const RESTRICTED_ALLOWED_ROUTES = ['/', '/billing', '/subscriptions', '/user-details', '/my-plan'];

  // Verifica se uma rota é permitida para acesso restrito
  const isRouteAllowedForRestricted = (path) => {
    if (!path) return false;
    return RESTRICTED_ALLOWED_ROUTES.some(route =>
      path === route || path.startsWith(route + '/')
    );
  };

  return (
    <UserContext.Provider
      value={{
        // Estados principais
        user,
        setUser,
        logged,
        setLogged,
        token,
        setToken,
        isInitializing, // NOVO: Estado de inicialização

        superuser,
        bottyData,
        setBottyData,
        fetchWaAccount,
        settings,
        accountLoading,
        setAccountLoading,

        // Estados de features/permissões
        userFeatures,
        permissionsLoading,
        permissionsError,

        // ========== FUNÇÕES DE PLANOS ==========
        userPlan,
        setUserPlan,
        getUserPlan,
        isUserClientPlan,
        isUserStarterPlan,
        isUserScalePlan,
        userHasFeatureAccess,
        getUserPlanCategory,
        getUserPlanLabel,

        // Sessão de suporte
        isSupportSession,
        checkSupportSession,
        canPerformAdminActions,

        // Funções de permissão por ID
        hasPermission,
        hasPermissionBySlug,
        canPerformActionWithUsage,
        getUsageInfo,
        getFeatureById,
        getFeaturesByCategory,
        getAvailableFeatures,
        getAvailableFeatureIds,

        // Funções de usuário
        canCreateUsers,
        canManagePermissions,
        canCreateUserWithLevel,
        loadUserPermissions,
        refreshPermissions,
        clearPermissionsCache,

        // Estados e funções do novo JWT
        accountContext,
        accountContextLoaded,
        accountFeatures,
        loadAccountData,

        // Helpers para tipo de conta
        isPersonalAccount,
        isOrganization,
        isSubaccount,
        canCreateSubaccounts,
        canDeployAgents,
        getAccountType,
        getOrganizationInfo,
        getSubaccountLimitations,
        getMasterAccountId,
        isAgentsV2Only,

        // Helpers para features
        hasFeature,
        getActiveFeatures,
        canPerformAction,

        // Acesso restrito por plano
        restrictedAccess,
        restrictionReason,
        isRestrictedAccess,
        getRestrictionReason,
        isRouteAllowedForRestricted,
        RESTRICTED_ALLOWED_ROUTES,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default UserContext;
